import React from 'react';
import './ProductFormByType.css';

/**
 * Beleuchtungs-spezifisches Formular
 * Beinhaltet: Watt, µmol/s, Effizienz, Zeltgröße, Lebensdauer, Smartsteuerung, Dimming
 */
function LightingForm({ formData, handleChange, handlePhaseChange, disabled }) {
  const growTentSizeToArea = {
    '40x40': '0.16',
    '40x60': '0.24',
    '40x80': '0.32',
    '40x100': '0.40',
    '60x60': '0.36',
    '60x100': '0.60',
    '60x120': '0.72',
    '60x150': '0.90',
    '80x80': '0.64',
    '80x100': '0.80',
    '80x120': '0.96',
    '80x150': '1.20',
    '80x200': '1.60',
    '100x100': '1.00',
    '100x150': '1.50',
    '100x200': '2.00',
    '120x120': '1.44',
    '120x150': '1.80',
    '120x180': '2.16',
    '120x200': '2.40',
    '120x240': '2.88',
    '150x150': '2.25',
    '150x200': '3.00',
    '150x240': '3.60',
    '200x200': '4.00',
    '240x240': '5.76'
  };

  const areaToGrowTentSize = Object.entries(growTentSizeToArea).reduce((acc, [size, area]) => {
    acc[area] = size;
    return acc;
  }, {});

  const handleGrowTentChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };

    if (name === 'growTentSize' && growTentSizeToArea[value]) {
      newFormData.recommendedArea = growTentSizeToArea[value];
    }
    if (name === 'recommendedArea' && areaToGrowTentSize[value]) {
      newFormData.growTentSize = areaToGrowTentSize[value];
    }

    handleChange({ target: { name, value: newFormData[name] } });
    if (name === 'growTentSize' || name === 'recommendedArea') {
      // Zusätzliche Änderungen weitergeben
      const otherField = name === 'growTentSize' ? 'recommendedArea' : 'growTentSize';
      handleChange({ target: { name: otherField, value: newFormData[otherField] } });
    }
  };

  return (
    <div className="product-type-form-section">
      <h3>💡 Beleuchtungs-Spezifikationen</h3>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="watts">💪 Watt</label>
          <input
            type="number"
            id="watts"
            name="watts"
            value={formData.watts || ''}
            onChange={handleChange}
            placeholder="z.B. 100"
            step="0.1"
            disabled={disabled}
          />
        </div>

        <div className="form-group">
          <label htmlFor="umolPerSecond">💡 µmol/s</label>
          <input
            type="number"
            id="umolPerSecond"
            name="umolPerSecond"
            value={formData.umolPerSecond || ''}
            onChange={handleChange}
            placeholder="z.B. 500"
            step="0.1"
            disabled={disabled}
          />
        </div>

        <div className="form-group">
          <label htmlFor="efficiency">📊 Effizienz (µmol/J)</label>
          <input
            type="number"
            id="efficiency"
            name="efficiency"
            value={formData.efficiency || ''}
            onChange={handleChange}
            placeholder="z.B. 2.5"
            step="0.01"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Phasen (optional)</label>
          <div className="phase-checkboxes">
            {['Blüte', 'Wachstum', 'Anzucht'].map(phase => (
              <label key={phase} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.phases?.includes(phase) || false}
                  onChange={() => handlePhaseChange(phase)}
                  disabled={disabled}
                />
                <span>{phase}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="growTentSize">🎪 Growzelt Größe</label>
          <select
            id="growTentSize"
            name="growTentSize"
            value={formData.growTentSize || ''}
            onChange={handleGrowTentChange}
            disabled={disabled}
          >
            <option value="">-- Bitte wählen --</option>
            <optgroup label="Kleine Zelte">
              <option value="40x40">40 x 40 cm (0.16 m²)</option>
              <option value="40x60">40 x 60 cm (0.24 m²)</option>
              <option value="40x80">40 x 80 cm (0.32 m²)</option>
              <option value="40x100">40 x 100 cm (0.40 m²)</option>
            </optgroup>
            <optgroup label="Mittlere Zelte">
              <option value="60x60">60 x 60 cm (0.36 m²)</option>
              <option value="60x100">60 x 100 cm (0.60 m²)</option>
              <option value="60x120">60 x 120 cm (0.72 m²)</option>
              <option value="60x150">60 x 150 cm (0.90 m²)</option>
            </optgroup>
            <optgroup label="Große Zelte">
              <option value="80x80">80 x 80 cm (0.64 m²)</option>
              <option value="80x100">80 x 100 cm (0.80 m²)</option>
              <option value="80x120">80 x 120 cm (0.96 m²)</option>
              <option value="80x150">80 x 150 cm (1.20 m²)</option>
              <option value="80x200">80 x 200 cm (1.60 m²)</option>
            </optgroup>
            <optgroup label="Profi Zelte">
              <option value="100x100">100 x 100 cm (1.00 m²)</option>
              <option value="100x150">100 x 150 cm (1.50 m²)</option>
              <option value="100x200">100 x 200 cm (2.00 m²)</option>
              <option value="120x120">120 x 120 cm (1.44 m²)</option>
              <option value="120x150">120 x 150 cm (1.80 m²)</option>
              <option value="120x180">120 x 180 cm (2.16 m²)</option>
              <option value="120x200">120 x 200 cm (2.40 m²)</option>
              <option value="120x240">120 x 240 cm (2.88 m²)</option>
            </optgroup>
            <optgroup label="Großzelte">
              <option value="150x150">150 x 150 cm (2.25 m²)</option>
              <option value="150x200">150 x 200 cm (3.00 m²)</option>
              <option value="150x240">150 x 240 cm (3.60 m²)</option>
              <option value="200x200">200 x 200 cm (4.00 m²)</option>
              <option value="240x240">240 x 240 cm (5.76 m²)</option>
            </optgroup>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="recommendedArea">📐 Empfohlene Fläche (m²)</label>
          <select
            id="recommendedArea"
            name="recommendedArea"
            value={formData.recommendedArea || ''}
            onChange={handleGrowTentChange}
            disabled={disabled}
          >
            <option value="">-- Bitte wählen --</option>
            <optgroup label="Kleine Zelte">
              <option value="0.16">0.16 m² (40x40)</option>
              <option value="0.24">0.24 m² (40x60)</option>
              <option value="0.32">0.32 m² (40x80)</option>
              <option value="0.40">0.40 m² (40x100)</option>
            </optgroup>
            <optgroup label="Mittlere Zelte">
              <option value="0.36">0.36 m² (60x60)</option>
              <option value="0.60">0.60 m² (60x100)</option>
              <option value="0.72">0.72 m² (60x120)</option>
              <option value="0.90">0.90 m² (60x150)</option>
            </optgroup>
            <optgroup label="Große Zelte">
              <option value="0.64">0.64 m² (80x80)</option>
              <option value="0.80">0.80 m² (80x100)</option>
              <option value="0.96">0.96 m² (80x120)</option>
              <option value="1.20">1.20 m² (80x150)</option>
              <option value="1.60">1.60 m² (80x200)</option>
            </optgroup>
            <optgroup label="Profi Zelte">
              <option value="1.00">1.00 m² (100x100)</option>
              <option value="1.50">1.50 m² (100x150)</option>
              <option value="2.00">2.00 m² (100x200)</option>
              <option value="1.44">1.44 m² (120x120)</option>
              <option value="1.80">1.80 m² (120x150)</option>
              <option value="2.16">2.16 m² (120x180)</option>
              <option value="2.40">2.40 m² (120x200)</option>
              <option value="2.88">2.88 m² (120x240)</option>
            </optgroup>
            <optgroup label="Großzelte">
              <option value="2.25">2.25 m² (150x150)</option>
              <option value="3.00">3.00 m² (150x200)</option>
              <option value="3.60">3.60 m² (150x240)</option>
              <option value="4.00">4.00 m² (200x200)</option>
              <option value="5.76">5.76 m² (240x240)</option>
            </optgroup>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="lifespan">⏱️ Lebensdauer (Stunden)</label>
          <input
            type="number"
            id="lifespan"
            name="lifespan"
            value={formData.lifespan || ''}
            onChange={handleChange}
            placeholder="z.B. 50000"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="smartControl">🎛️ Smartsteuerung</label>
          <select
            id="smartControl"
            name="smartControl"
            value={formData.smartControl || ''}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">-- Keine Angabe --</option>
            <option value="Smartsteuerung">Smartsteuerung</option>
            <option value="Smartsteuerung möglich">Smartsteuerung möglich</option>
            <option value="keine Smartsteuerung">Keine Smartsteuerung</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="dimmable">🔦 Dimming</label>
          <select
            id="dimmable"
            name="dimmable"
            value={formData.dimmable || ''}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">-- Keine Angabe --</option>
            <option value="nicht dimmbar">Nicht dimmbar</option>
            <option value="PWM Dimming">PWM Dimming</option>
            <option value="CCR-Dimming">CCR-Dimming</option>
            <option value="Hybrides Dimming">Hybrides Dimming</option>
            <option value="Manuelles Dimming">Manuelles Dimming</option>
          </select>
        </div>

        <div className="form-group">
          <label>📦 Dimmer/Kabel im Lieferumfang</label>
          <div className="checkbox-label">
            <input
              type="checkbox"
              id="dimmerIncluded"
              name="dimmerIncluded"
              checked={formData.dimmerIncluded || false}
              onChange={(e) => {
                handleChange({ target: { name: 'dimmerIncluded', value: e.target.checked } });
              }}
              disabled={disabled}
            />
            <span>Dimmer/Kabel im Lieferumfang enthalten</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LightingForm;
